import React from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import Spinner from 'reactstrap/lib/Spinner';
import { LoyaltyProgram as LoyaltyProgramCore } from 'core/components';
import { withUser, withUserAgent, withCoreComponent } from 'core/hocs';
import { USER_FIELDS, DEVICES_TYPES, LOYALTY_POINTS_STATUSES } from 'core/constants';
import { isEmptyOrNil } from 'core/helpers';

import { LoyaltyCard } from 'components/loyalty-program/loyalty-card/loyalty-card';
import { IconDottedLineLg1 } from 'components/icons/loyalty-dotted-lines/dotted-lines-lg/dotted-line-lg-1';
import { IconDottedLineLg2 } from 'components/icons/loyalty-dotted-lines/dotted-lines-lg/dotted-line-lg-2';
import { IconDottedLineLg3 } from 'components/icons/loyalty-dotted-lines/dotted-lines-lg/dotted-line-lg-3';
import { IconDottedLineLg4 } from 'components/icons/loyalty-dotted-lines/dotted-lines-lg/dotted-line-lg-4';
import { IconDottedLineSm1 } from 'components/icons/loyalty-dotted-lines/dotted-lines-sm/dotted-line-sm-1';
import { IconDottedLineSm2 } from 'components/icons/loyalty-dotted-lines/dotted-lines-sm/dotted-line-sm-2';
import { IconDottedLineSm3 } from 'components/icons/loyalty-dotted-lines/dotted-lines-sm/dotted-line-sm-3';
import { IconDottedLineSm4 } from 'components/icons/loyalty-dotted-lines/dotted-lines-sm/dotted-line-sm-4';
import { FREESPINS_INFO_MOBILE, FREESPINS_INFO_DESKTOP, CURRENCY_SYMBOLS } from '../../constants';

import './loyalty-program.scss';

const dottedLinesSm = [
  IconDottedLineSm1,
  IconDottedLineSm2,
  IconDottedLineSm3,
  IconDottedLineSm4,
];

const dottedLinesLg = [
  IconDottedLineLg1,
  IconDottedLineLg2,
  IconDottedLineLg3,
  IconDottedLineLg4,
];

const loyaltyLevelsClassNames = [
  'align-self-sm-start',
  'align-self-sm-end',
  'align-self-sm-start',
  'align-self-sm-end ',
  'align-self-sm-start',
];

const STATUS_LEVELS = {
  [LOYALTY_POINTS_STATUSES.BRONZE]: 1,
  [LOYALTY_POINTS_STATUSES.SILVER]: 2,
  [LOYALTY_POINTS_STATUSES.GOLD]: 3,
  [LOYALTY_POINTS_STATUSES.PLATINUM]: 4,
  [LOYALTY_POINTS_STATUSES.DIAMOND]: 5,
};

export const LoyaltyProgramUI = ({
  loyaltyLevels,
  userData: { characterId, vipStatus },
  deviceType, userBalance: { currency },
  isUserLoggedIn, defaultCurrency
}) => {
  const userCurrency = isUserLoggedIn ? currency : defaultCurrency;

  const targetVipStatus = R.compose(
    R.toLower,
    R.trim,
  )(vipStatus || LOYALTY_POINTS_STATUSES.BRONZE);

  const activeLevelOrder = STATUS_LEVELS[targetVipStatus];

  return (
    <div className="loyalty-card-container d-flex flex-column align-items-center mx-sm-auto mt-6">
      {!isEmptyOrNil(loyaltyLevels)
        ? loyaltyLevels.map((level, idx) => {
          const {
            levelTitle, sortOrderIndex,
            gift, ...props
          } = level;

          let freespins = {};

          const [freespinsGameId, freespinsGameName, freespinsGameProducer] = deviceType === DEVICES_TYPES.MOBILE
            ? FREESPINS_INFO_MOBILE
            : FREESPINS_INFO_DESKTOP;

          if (R.propOr(null, 'freespinsAmount', level)) {
            const {
              [freespinsGameId]: gameId,
              [freespinsGameName]: gameName,
              [freespinsGameProducer]: gameProducer,
            } = level;

            freespins = {
              amount: level.freespinsAmount,
              game: {
                gameId: gameId.intlKey,
                gameName: gameName.intlKey,
                gameProducer: gameProducer.intlKey
              },
            };
          }

          const userGift = R.path(['currencies', userCurrency], gift);

          return (
            <LoyaltyCard
              {...props}
              key={level.id}
              name={levelTitle.intlKey}
              characterId={characterId}
              order={sortOrderIndex}
              gift={userGift}
              imgUrl={level.imageUrl}
              currencySymbol={CURRENCY_SYMBOLS[userCurrency]}
              freespins={freespins || {}}
              alignClassName={loyaltyLevelsClassNames[idx]}
              className={`loyalty-card-${sortOrderIndex}`}
              isActive={activeLevelOrder === sortOrderIndex}
              isLevelAccomplished={R.lt(sortOrderIndex, activeLevelOrder)}
              dottedLineIcon={[dottedLinesSm[idx], dottedLinesLg[idx]]}
              dottedLineWrapperClassName={`dotted-line-wrapper-${sortOrderIndex}`}
            />
          );
        }) : (
          <div className="loyalty-program-preloader mt-13 mt-md-7 mt-lg-13">
            <Spinner className="p-2_5" />
          </div>
        )}

    </div>
  );
};

LoyaltyProgramUI.propTypes = {
  loyaltyLevels: PropTypes.arrayOf(PropTypes.shape()),
  loyaltyPoints: PropTypes.shape(),
  isUserLoggedIn: PropTypes.bool.isRequired,
  userData: PropTypes.shape(),
  isCharacterInProgress: PropTypes.bool,
  deviceType: PropTypes.string.isRequired,
  userBalance: PropTypes.shape().isRequired,
  currency: PropTypes.string,
  defaultCurrency: PropTypes.string.isRequired
};

LoyaltyProgramUI.defaultProps = {
  loyaltyLevels: [],
  loyaltyPoints: {},
  currency: '',
  userData: {
    characterId: 1,
  },
  isCharacterInProgress: false,
};

export const LoyaltyProgram = withCoreComponent(
  LoyaltyProgramCore,
  withUser(
    withUserAgent(LoyaltyProgramUI),
    [
      USER_FIELDS.LOYALTY_POINTS,
      USER_FIELDS.USER_DATA,
      USER_FIELDS.IS_CHARACTER_IN_PROGRESS,
      USER_FIELDS.USER_BALANCE,
      USER_FIELDS.IS_USER_LOGGED_IN,
      USER_FIELDS.DEFAULT_CURRENCY,
    ]
  )
);
